import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    HostBinding,
    ViewChildren,
    QueryList,
    output,
    input,
    effect,
} from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';
import { ITask2ReportItem } from '../../../fi-task-v2-report';
import { FiTaskCategoryType, FiTaskType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskReportType, FiTaskV2ReportAssigneesCellType } from '../../../fi-task-v2-report-enums';
import { IItemsListAction2 } from '@fi-app/shared/fi-items-list/fi-items-list';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiTaskV2ReportHelperService } from '../../../services/fi-task-v2-report-helper.service';
import { ACTIONS_TYPES, ITaskActionBuilderContext, TaskActionsBuilderService } from './task-actions.builder.service';
import { MatSortDirection } from '@fi-shared/types';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { isDefined } from '@fi-shared/utils';
import { FiWrapperTableMatMenuCellComponent } from '@fi-app/shared/fi-wrapper-table/cells/fi-wrapper-table-mat-menu-cell/fi-wrapper-table-mat-menu-cell.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';
import { FiTaskStatusesConfig } from '@fi-app/task-v2/fi-task-v2-consts';

@Component({
    selector: 'fi-widget-details-table',
    templateUrl: './widget-details-table.component.html',
    styleUrls: ['./widget-details-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-widget-details-table' },
    standalone: false,
})
export class WidgetDetailsTableComponent implements OnInit, OnDestroy {
    @Input({ required: true }) sortColumn: string;
    @Input({ required: true }) sortDirection: SortDirection;
    @Input({ required: true }) taskReportType: FiTaskReportType;
    public items = input.required<ITask2ReportItem[]>();
    public loading = input<boolean>(false);

    public sortChanged = output<Sort>();
    public scrollToBottom = output<void>();
    public taskStatusUpdated = output<ITask2ReportItem>();

    protected readonly actions: ReadonlyArray<Readonly<IItemsListAction2<ITask2ReportItem>>>;
    // private _taskStatusUpdated$ = new Subject<ITask2ReportItem>();
    private actionExecuted$ = new Subject<{ type: ACTIONS_TYPES; item: ITask2ReportItem }>();

    @HostBinding('class.all-tasks-report')
    get isAllTasksReport(): boolean {
        return this.taskReportType === FiTaskReportType.All;
    }
    @HostBinding('class.overdue-tasks-report')
    get isOverdueTasksReport(): boolean {
        return this.taskReportType === FiTaskReportType.Overdue;
    }
    @HostBinding('class.checklist-tasks-report')
    get isChecklistTasksReport(): boolean {
        return this.taskReportType === FiTaskReportType.ChecklistTasks;
    }

    protected isActionsHidden$ = new BehaviorSubject<boolean>(false);
    private _matMenuCells: QueryList<FiWrapperTableMatMenuCellComponent<ITask2ReportItem>>;
    @ViewChildren(FiWrapperTableMatMenuCellComponent) set matMenuCells(
        value: QueryList<FiWrapperTableMatMenuCellComponent<ITask2ReportItem>>,
    ) {
        this._matMenuCells = value;
        this.setActionsVisibility();
    }

    protected readonly FiTaskStatusesConfig = FiTaskStatusesConfig;

    constructor(
        private fiTaskV2ReportHelperService: FiTaskV2ReportHelperService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private cdr: ChangeDetectorRef,
        private taskActionsBuilderService: TaskActionsBuilderService,
        private fiTaskSyncService: FiTaskSyncService,
    ) {
        this.actions = this.initializeActions();
        effect(() => {
            const items = this.items();
            this.setActionsVisibility();
        });
    }

    public ngOnInit(): void {
        this.subscribeToTaskStatusUpdated();
        const currentTaskItem = this.fiTaskV2ReportHelperService.getCurrentTaskItem();
        if (currentTaskItem) {
            this.openTaskOverview(currentTaskItem);
        }
    }

    private setActionsVisibility(): void {
        setTimeout(() => {
            if (!isDefined(this._matMenuCells)) {
                return;
            }
            const isActionsVisible = this._matMenuCells.some((matMenuCell) => {
                return !matMenuCell.matIconCheckboxMenuComponent.hidden;
            });
            this.isActionsHidden$.next(!isActionsVisible);
        }, 0);
    }

    private subscribeToTaskStatusUpdated(): void {
        this.fiTaskSyncService.updatedTasksSyncResult$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((syncResult) => {
            syncResult.updatedItems.forEach((updatedTask) => {
                const currentTaskReportItem = this.items().find((item) => item.Id === updatedTask.Id);
                if (currentTaskReportItem) {
                    currentTaskReportItem.Status = updatedTask.Status;
                    currentTaskReportItem.IsDeadlinePassed = updatedTask.IsDeadlinePassed;
                    currentTaskReportItem.ReportStatus = this.fiTaskV2ReportHelperService.getReportStatus(
                        updatedTask.Status,
                        updatedTask.IsDeadlinePassed,
                    );
                    this.cdr.markForCheck();
                    if (updatedTask.Id === this.openedTaskReportItem$.value?.Id) {
                        this.openedTaskReportItem$.next(currentTaskReportItem);
                    }
                }
            });
        });

        this.actionExecuted$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter((action) => action.type === ACTIONS_TYPES.UPDATE_STATUS),
            )
            .subscribe(() => {
                const currentTaskReportItem = this.openedTaskReportItem$.value;
                if (isDefined(currentTaskReportItem)) {
                    this.openedTaskReportItem$.next(this.items().find((item) => item.Id === currentTaskReportItem.Id));
                }
                this.taskStatusUpdated.emit(currentTaskReportItem);
            });
    }

    private initializeActions(): ReadonlyArray<Readonly<IItemsListAction2<ITask2ReportItem>>> {
        const actionContext: ITaskActionBuilderContext = {
            cdr: this.cdr,
            actionExecuted$: this.actionExecuted$,
        };
        return this.taskActionsBuilderService.createTaskActions(actionContext);
    }

    protected onSortChanged(event: Sort) {
        this.sortChanged.emit(event);
    }

    protected onScrollToBottom() {
        this.scrollToBottom.emit();
    }

    private openedTaskReportItem$ = new BehaviorSubject<ITask2ReportItem>(undefined);
    protected openTaskOverview(item: ITask2ReportItem, $event?: MouseEvent): void {
        $event?.preventDefault();
        this.openedTaskReportItem$.next(item);
        this.fiTaskV2ReportHelperService
            .openTaskViewerDialog(this.openedTaskReportItem$, this.actionExecuted$)
            .afterClosed(() => {
                this.openedTaskReportItem$.next(undefined);
            });
    }

    protected getCategoriesText(item: ITask2ReportItem): string {
        if (this.isCategoryColumnVisible()) {
            return this.fiTaskV2HelperService.getTaskCategoriesText(item.Categories);
        }
        return '';
    }

    protected getTaskCategoryText(category: FiTaskCategoryType): string {
        return this.fiTaskV2HelperService.getTaskCategoryText(category);
    }

    protected isSystemTask(item: ITask2ReportItem): boolean {
        return item.TaskType !== FiTaskType.Common;
    }

    protected isCompletedAfterDeadline(item: ITask2ReportItem): boolean {
        return this.fiTaskV2ReportHelperService.isTaskCompletedAfterDeadline(item);
    }

    protected isCategoryColumnVisible(): boolean {
        return (
            this.taskReportType === FiTaskReportType.All ||
            this.taskReportType === FiTaskReportType.Overdue ||
            this.taskReportType === FiTaskReportType.ChecklistTasks
        );
    }

    protected isPrivateTasksReport(): boolean {
        return this.taskReportType === FiTaskReportType.Private;
    }

    protected isTaskOverdue(item: ITask2ReportItem): boolean {
        return item.Status !== FiTaskStatusType.Completed && item.IsOverdue;
    }

    protected isOverdueReport(): boolean {
        return this.taskReportType === FiTaskReportType.Overdue;
    }

    protected getDeadlineColumnStartSort() {
        return this.taskReportType !== FiTaskReportType.Overdue
            ? MatSortDirection.Descending
            : MatSortDirection.Ascending;
    }

    protected readonly MatSortDirection = MatSortDirection;

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiStateNames = FiStateNames;
    protected readonly FiTaskV2ReportAssigneesCellType = FiTaskV2ReportAssigneesCellType;
}
